import React, { useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import cx from 'classnames';
import { uniqueId } from 'lodash';
import { I18n } from 'common/i18n';
import { DragHandleIcon } from './DragHandleIcon';
import { DragDropElementWrapper } from './index';
import { getSortableStyle } from './sortableStyles';

const scope = 'common';

interface SortableListElementProps<T = unknown> {
  item: DragDropElementWrapper<T>;
  isDragOverlay?: boolean;
  iconName?: string;
}

export const SortableListElement = <T = unknown,>({
  item,
  isDragOverlay = false,
  iconName = 'drag_indicator',
}: SortableListElementProps<T>) => {
  // Generate a stable default ID if none provided
  const sortableId = useMemo(() => item.id || uniqueId('sortable-list-'), [item.id]);
  const ariaLabel = I18n.t('drag_to_reorder_aria_label', { scope });

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    data: {
      type: 'sortable-item',
      item: item,
    },
    animateLayoutChanges: () => false,
  });

  const style = getSortableStyle({
    transform,
    transition: transition || undefined,
    isDragging,
    isDragOverlay,
    additionalStyles: {
      display: 'flex',
    },
  });

  return (
    <li
      {...item.additionalItemProps}
      className={cx('drag-element', item.className, { 'is-dragging': isDragging })}
      ref={setNodeRef}
      style={style}
    >
      <div {...attributes} {...listeners} className="drag-handle" aria-label={ariaLabel}>
        <DragHandleIcon name={iconName} isDraggable={item.dragAndDroppable} />
      </div>
      {item.element}
    </li>
  );
};
